import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesInventarioAPI, ordenesCatalogosAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { ColumnSelector } from '../../components/ui/column-selector';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Package,
    Plus,
    Search,
    ArrowLeft,
    Edit,
    Trash2,
    Loader2,
    CheckCircle,
    XCircle,
    Wrench,
    Box,
    Tag,
} from 'lucide-react';

const STOCK_COLUMNS = [
    { key: 'imei', label: 'IMEI', required: true, defaultVisible: true },
    { key: 'tipo', label: 'Tipo', defaultVisible: true },
    { key: 'marca', label: 'Marca', defaultVisible: true },
    { key: 'modelo', label: 'Modelo', defaultVisible: true },
    { key: 'estado', label: 'Estado', defaultVisible: true },
];

const ESTADO_CONFIG = {
    disponible: { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle, label: 'Disponible' },
    instalado: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Box, label: 'Instalado' },
    en_reparacion: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Wrench, label: 'En Reparación' },
    dado_de_baja: { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Dado de Baja' },
};

const initialFormState = {
    tipo_dispositivo: '',
    imei: '',
    marca: '',
    modelo: '',
    estado: 'disponible',
    observaciones: '',
};

export default function OrdenesInventarioPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [dispositivos, setDispositivos] = useState([]);
    const [tiposDispositivo, setTiposDispositivo] = useState([]);
    const [resumen, setResumen] = useState({});
    const [search, setSearch] = useState('');
    const [tipoFilter, setTipoFilter] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    
    // Selección múltiple
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Bulk edit
    const [bulkMarca, setBulkMarca] = useState('');
    const [bulkModelo, setBulkModelo] = useState('');
    
    // Columnas visibles
    const [visibleColumns, setVisibleColumns] = useState(
        STOCK_COLUMNS.filter(c => c.defaultVisible !== false).map(c => c.key)
    );
    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadDispositivos();
    }, [tipoFilter, estadoFilter]);

    const loadData = async () => {
        try {
            const [tiposRes, resumenRes] = await Promise.all([
                ordenesCatalogosAPI.getTiposDispositivo(),
                ordenesInventarioAPI.getResumen()
            ]);
            setTiposDispositivo(tiposRes.data || []);
            setResumen(resumenRes.data || {});
        } catch (err) {
            console.error('Error loading data:', err);
        }
    };

    const loadDispositivos = async () => {
        setLoading(true);
        try {
            const params = {};
            if (tipoFilter && tipoFilter !== '_all') params.tipo = tipoFilter;
            if (estadoFilter && estadoFilter !== '_all') params.estado = estadoFilter;
            const res = await ordenesInventarioAPI.getAll(params);
            setDispositivos(res.data || []);
            setSelectedIds([]);
        } catch (err) {
            console.error('Error loading dispositivos:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingItem(null);
        setFormData(initialFormState);
        setShowDialog(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            tipo_dispositivo: item.tipo_dispositivo || '',
            imei: item.imei || item.serial || '',
            marca: item.marca || '',
            modelo: item.modelo || '',
            estado: item.estado || 'disponible',
            observaciones: item.observaciones || '',
        });
        setShowDialog(true);
    };

    const handleOpenDelete = (item) => {
        setEditingItem(item);
        setShowDeleteDialog(true);
    };

    const handleSave = async () => {
        if (!formData.imei) {
            toast.error('El IMEI/Serial es requerido');
            return;
        }

        setSaving(true);
        try {
            const dataToSend = {
                ...formData,
                serial: formData.imei, // Backward compatibility
            };
            
            if (editingItem) {
                await ordenesInventarioAPI.update(editingItem.id, dataToSend);
                toast.success('Dispositivo actualizado');
            } else {
                await ordenesInventarioAPI.create(dataToSend);
                toast.success('Dispositivo agregado al inventario');
            }
            setShowDialog(false);
            loadDispositivos();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await ordenesInventarioAPI.delete(editingItem.id);
            toast.success('Dispositivo eliminado');
            setShowDeleteDialog(false);
            loadDispositivos();
            loadData();
        } catch (err) {
            toast.error('Error al eliminar dispositivo');
        } finally {
            setSaving(false);
        }
    };

    // Selección múltiple
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(filteredDispositivos.map(d => d.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(i => i !== id));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        
        setSaving(true);
        try {
            const res = await ordenesInventarioAPI.deleteBulk(selectedIds);
            toast.success(res.data.message);
            setShowBulkDeleteDialog(false);
            setSelectedIds([]);
            loadDispositivos();
            loadData();
        } catch (err) {
            toast.error('Error al eliminar dispositivos');
        } finally {
            setSaving(false);
        }
    };

    const handleBulkEdit = async () => {
        if (selectedIds.length === 0) return;
        if (!bulkMarca && !bulkModelo) {
            toast.error('Ingrese al menos marca o modelo');
            return;
        }
        
        setSaving(true);
        try {
            const res = await ordenesInventarioAPI.updateBulk(selectedIds, bulkMarca || null, bulkModelo || null);
            toast.success(res.data.message);
            setShowBulkEditDialog(false);
            setSelectedIds([]);
            setBulkMarca('');
            setBulkModelo('');
            loadDispositivos();
        } catch (err) {
            toast.error('Error al actualizar dispositivos');
        } finally {
            setSaving(false);
        }
    };

    const filteredDispositivos = dispositivos.filter(d => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            d.imei?.toLowerCase().includes(searchLower) ||
            d.serial?.toLowerCase().includes(searchLower) ||
            d.marca?.toLowerCase().includes(searchLower) ||
            d.modelo?.toLowerCase().includes(searchLower)
        );
    });

    const allSelected = filteredDispositivos.length > 0 && selectedIds.length === filteredDispositivos.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < filteredDispositivos.length;

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Package className="h-6 w-6" />
                            Stock Disponible
                        </h1>
                        <p className="text-muted-foreground">Inventario de dispositivos</p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {selectedIds.length > 0 && (
                        <>
                            <Button 
                                variant="outline"
                                onClick={() => setShowBulkEditDialog(true)}
                                data-testid="bulk-edit-btn"
                            >
                                <Tag className="h-4 w-4 mr-2" />
                                Asignar Marca/Modelo ({selectedIds.length})
                            </Button>
                            <Button 
                                variant="destructive"
                                onClick={() => setShowBulkDeleteDialog(true)}
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar ({selectedIds.length})
                            </Button>
                        </>
                    )}
                    {tienePermiso('gestionar_inventario') && (
                        <Button onClick={handleOpenCreate}>
                            <Plus className="h-4 w-4 mr-2" />
                            Agregar Dispositivo
                        </Button>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(ESTADO_CONFIG).map(([estado, config]) => {
                    const count = resumen[estado] || 0;
                    const Icon = config.icon;
                    return (
                        <Card key={estado} className={`${config.color} border`}>
                            <CardContent className="p-4 flex items-center gap-3">
                                <Icon className="h-8 w-8" />
                                <div>
                                    <p className="text-2xl font-bold">{count}</p>
                                    <p className="text-sm">{config.label}</p>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por IMEI, marca, modelo..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                    />
                </div>
                
                <Select value={tipoFilter || "_all"} onValueChange={(v) => setTipoFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos los tipos</SelectItem>
                        {tiposDispositivo.map(t => (
                            <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <Select value={estadoFilter || "_all"} onValueChange={(v) => setEstadoFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos los estados</SelectItem>
                        {Object.entries(ESTADO_CONFIG).map(([codigo, config]) => (
                            <SelectItem key={codigo} value={codigo}>{config.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <ColumnSelector
                    columns={STOCK_COLUMNS}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={setVisibleColumns}
                    storageKey="stock-columns"
                />
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : filteredDispositivos.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay dispositivos en el inventario</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">
                                        <Checkbox 
                                            checked={allSelected}
                                            ref={(el) => el && (el.indeterminate = someSelected)}
                                            onCheckedChange={handleSelectAll}
                                        />
                                    </TableHead>
                                    {visibleColumns.includes('imei') && <TableHead>IMEI</TableHead>}
                                    {visibleColumns.includes('tipo') && <TableHead>Tipo</TableHead>}
                                    {visibleColumns.includes('marca') && <TableHead>Marca</TableHead>}
                                    {visibleColumns.includes('modelo') && <TableHead>Modelo</TableHead>}
                                    {visibleColumns.includes('estado') && <TableHead>Estado</TableHead>}
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDispositivos.map((item) => {
                                    const estadoConfig = ESTADO_CONFIG[item.estado] || ESTADO_CONFIG.disponible;
                                    const Icon = estadoConfig.icon;
                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Checkbox 
                                                    checked={selectedIds.includes(item.id)}
                                                    onCheckedChange={(checked) => handleSelectOne(item.id, checked)}
                                                />
                                            </TableCell>
                                            {visibleColumns.includes('imei') && (
                                                <TableCell className="font-mono">{item.imei || item.serial || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('tipo') && (
                                                <TableCell>
                                                    {tiposDispositivo.find(t => t.codigo === item.tipo_dispositivo)?.nombre || item.tipo_dispositivo || '-'}
                                                </TableCell>
                                            )}
                                            {visibleColumns.includes('marca') && (
                                                <TableCell>{item.marca || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('modelo') && (
                                                <TableCell>{item.modelo || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('estado') && (
                                                <TableCell>
                                                    <Badge className={`${estadoConfig.color} gap-1`}>
                                                        <Icon className="h-3 w-3" />
                                                        {estadoConfig.label}
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    {tienePermiso('gestionar_inventario') && (
                                                        <>
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(item)}>
                                                                <Trash2 className="h-4 w-4 text-red-500" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Summary */}
            <div className="text-sm text-muted-foreground text-center">
                {selectedIds.length > 0 && (
                    <span className="text-primary font-medium">{selectedIds.length} seleccionados de </span>
                )}
                {filteredDispositivos.length} dispositivos en stock
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Editar Dispositivo' : 'Agregar Dispositivo'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>IMEI/Serial *</Label>
                            <Input
                                value={formData.imei}
                                onChange={(e) => setFormData({...formData, imei: e.target.value})}
                                placeholder="Ingrese IMEI o número de serie"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Tipo de Dispositivo</Label>
                            <Select value={formData.tipo_dispositivo || "_none"} onValueChange={(v) => setFormData({...formData, tipo_dispositivo: v === "_none" ? "" : v})}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">Sin especificar</SelectItem>
                                    {tiposDispositivo.map(t => (
                                        <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Marca</Label>
                                <Input
                                    value={formData.marca}
                                    onChange={(e) => setFormData({...formData, marca: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Modelo</Label>
                                <Input
                                    value={formData.modelo}
                                    onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Estado</Label>
                            <Select value={formData.estado} onValueChange={(v) => setFormData({...formData, estado: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(ESTADO_CONFIG).map(([codigo, config]) => (
                                        <SelectItem key={codigo} value={codigo}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editingItem ? 'Guardar' : 'Agregar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Dispositivo</DialogTitle>
                        <DialogDescription>
                            ¿Eliminar dispositivo <strong>{editingItem?.imei || editingItem?.serial}</strong>?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Dispositivos</DialogTitle>
                        <DialogDescription>
                            ¿Eliminar <strong>{selectedIds.length} dispositivos</strong> del inventario?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleBulkDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Eliminar {selectedIds.length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Edit Dialog */}
            <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar Marca/Modelo</DialogTitle>
                        <DialogDescription>
                            Asignar marca y modelo a <strong>{selectedIds.length} dispositivos</strong> seleccionados.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Marca</Label>
                            <Input
                                value={bulkMarca}
                                onChange={(e) => setBulkMarca(e.target.value)}
                                placeholder="Ej: Teltonika, Queclink"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Modelo</Label>
                            <Input
                                value={bulkModelo}
                                onChange={(e) => setBulkModelo(e.target.value)}
                                placeholder="Ej: FMC130, GV300"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>Cancelar</Button>
                        <Button onClick={handleBulkEdit} disabled={saving || (!bulkMarca && !bulkModelo)}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Asignar a {selectedIds.length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
